import React, { useEffect, useState, useMemo } from 'react'

const DEFAULT_AVATAR = '/bnb-profile-default.png'

/**
 * Confetti animation component - renders colorful falling confetti pieces
 * Used once at the banner level for full-width effect
 */
function Confetti() {
    const pieces = useMemo(() => {
        const colors = ['#c8a951','#ffffff','#003087','#f5e198','#d4af37']
        return Array.from({ length: 24 }, (_, i) => ({
            id: i, color: colors[i % colors.length],
            left: `${5 + (i * 3.8) % 90}%`,
            width: `${4 + (i % 5)}px`, height: `${5 + (i % 7)}px`,
            delay: `${(i * 0.4) % 3.5}s`, duration: `${2.5 + (i % 4) * 0.5}s`,
            isCircle: i % 3 === 0,
        }))
    }, [])
    return (
        <>
            <style>{`@keyframes bnbConfetti { 0% { transform:translateY(-10px) rotate(0deg); opacity:1; } 85% { opacity:0.85; } 100% { transform:translateY(240px) rotate(540deg); opacity:0; } }`}</style>
            {pieces.map(p => (
                <div key={p.id} style={{ position:'absolute', top:0, left:p.left, width:p.width, height:p.height, background:p.color, borderRadius:p.isCircle?'50%':'1px', animation:`bnbConfetti ${p.duration} ${p.delay} infinite linear`, opacity:0, pointerEvents:'none', zIndex:5 }} />
            ))}
        </>
    )
}

/**
 * WinnersCarousel - Displays winners with automatic rotation
 * Phase 1 (4s): Shows group of winners
 * Phase 2 (rotation every 8s): Shows individual contest/award highlights
 * 
 * @param {Array} winnerDetails - Group of winners for group phase display
 * @param {Array} highlights - Array of contest/award objects for single phase rotation
 */
function WinnersCarousel({ winnerDetails = [], highlights = [] }) {
    const [phase, setPhase] = useState('group')
    const [idx, setIdx]     = useState(0)
    const [fade, setFade]   = useState(true)

    useEffect(() => {
        if (highlights.length === 0) return
        const t1 = setTimeout(() => { setFade(false); setTimeout(() => { setPhase('single'); setIdx(1 % highlights.length); setFade(true) }, 1000) }, 4000)
        return () => clearTimeout(t1)
    }, [highlights.length])

    useEffect(() => {
        if (phase !== 'single' || highlights.length <= 1) return
        const iv = setInterval(() => { setFade(false); setTimeout(() => { setIdx(i => (i + 1) % highlights.length); setFade(true) }, 1000) }, 8000)
        return () => clearInterval(iv)
    }, [phase, highlights.length])

    const nameTag = name => (
        <div className="bnb-wc-name-tag-wrapper">
            <div className="bnb-wc-name-tag">{name}</div>
        </div>
    )



    if (highlights.length === 0) return null
    const h = highlights[idx]
    const winnerCount = h.winner_group ? h.winner_group.length : 1

    return (
        <div className="bnb-wc-base" style={{ opacity: fade ? 1 : 0 }} data-highlight-type={h.type} data-highlight-kind={h.kind} data-winner-count={winnerCount}>
            {h.kind === 'contest' ? (
                /* Contest: title + Round + course name + yardage to the LEFT of image */
                <div className="bnb-wc-img-col bnb-wc-contest-slide">
                    <div className="bnb-wc-contest-text-wrapper">
                        <div className="bnb-wc-contest-title">{h.title}</div>
                        
                        {h.subtitle && <div className="bnb-wc-contest-result">{h.subtitle}</div>}
                        {h.result_yards && <div className="bnb-wc-contest-result">{h.result_yards}</div>}
                    </div>
            {h.type === 'team' && h.winner_group ? (
                <div className="bnb-wc-image-group-container">
                    {h.winner_group.map((w, i) => (
                        <div key={i} className="bnb-wc-winner-item" style={{ marginLeft: i > 0 ? '-14px' : 0, zIndex: h.winner_group.length - i }}>
                            <img src={w.image_url||DEFAULT_AVATAR} alt={w.name} className={`bnb-wc-player-winner-img bnb-wc-player-winner-img-${h.winner_group.length}`} />
                            {nameTag(w.name.split(' ')[0])}
                        </div>
                    ))}
                </div>
            ) : (
                        <div className="bnb-wc-single-image-container">
                            <img src={h.winner_image_url||DEFAULT_AVATAR} alt={h.winner_name} className="bnb-wc-player-img" />
                            {nameTag(h.winner_name)}
                        </div>
                    )}
                </div>
            ) : (
                /* Award: title to the LEFT of image */
                h.type === 'team' && h.winner_group ? (
                    <div className="bnb-wc-img-col bnb-wc-winner-slide">
                        <div className="bnb-wc-winner-title">{h.title}</div>
                        <div className="bnb-wc-image-group-container">
                            {h.winner_group.map((w, i) => (
                                <div key={i} className="bnb-wc-winner-item" style={{ marginLeft: i > 0 ? '-14px' : 0, zIndex: h.winner_group.length - i }}>
                                    <img src={w.image_url||DEFAULT_AVATAR} alt={w.name} className={`bnb-wc-player-winner-img bnb-wc-player-winner-img-${h.winner_group.length}`} />
                                    {nameTag(w.name.split(' ')[0])}
                                </div>
                            ))}
                        </div>
                    </div>
                ) : (
                    
                    <div className="bnb-wc-img-col bnb-wc-award-slide">
                    <div className="bnb-wc-award-title">{h.title}</div>
                    <div className="bnb-wc-single-image-container">
                        <img src={h.winner_image_url||DEFAULT_AVATAR} alt={h.winner_name} className="bnb-wc-player-img" />
                        {nameTag(h.winner_name)}
                    </div>
                    </div>
    
                )
                
            )}
        </div>
    )
}

export { Confetti, WinnersCarousel }
